import { Array, pipe } from 'effect'
import { isNotUndefined } from 'effect/Predicate'

import type { ExpandCursorArea } from '../../recoil/cursor'
import { getRecordNodesWithInnerRowIndex, type RecordNodeWithInnerRowIndex } from '../../rsheet/util/innerRowRelated'
import type { RSheetColumn, RSheetRecordNode } from '../column'
import { calcCellHeight } from '../copyAndPaste/calcCellHeight'
import {
  type CellGroup,
  type CopyMatrix,
  type TransposedCopyMatrix,
  type TransposedCopyMatrixRow,
  transposeMatrix,
} from '../copyMatrix'
import { getArea } from '../cursor/cursorHelpers'
import type { RSheetsCursor } from '../cursor/cursorTypes'
import { type RelationPaste, resolveRelationPaste } from './relationPaste'

export interface GenerateChangeCandidatesArgument {
  copyMatrix: CopyMatrix
  records: RSheetRecordNode[]
  columns: Array<RSheetColumn<RSheetRecordNode>>
  cursor: RSheetsCursor | undefined
  // 貼り付け先カラムの参照先エンティティ型を解決する。クエリツリーを持つ呼出側で注入する（削除経路では () => undefined）
  getReferencedStreamName: (column: RSheetColumn<RSheetRecordNode>) => string | undefined
}

export type ChangeCandidate = UpdateChangeCandidate | InsertChangeCandidate

export interface UpdateChangeCandidate extends ChangeCandidateBase {
  recordId: string | undefined
}

type InsertChangeCandidate = ChangeCandidateBase

interface ChangeCandidateBase {
  column: RSheetColumn<RSheetRecordNode>
  value: unknown
  relationPaste: RelationPaste | undefined
  rowIndex: number
  innerRowIndex: number
  height: number
}

interface ChangeCandidatesResult {
  updates: UpdateChangeCandidate[]
  inserts: InsertChangeCandidate[]
}

const emptyChangeCandidatesResult = (): ChangeCandidatesResult => ({ updates: [], inserts: [] })

const mergeChangeCandidatesResult = (
  accumulator: ChangeCandidatesResult,
  current: ChangeCandidatesResult,
): ChangeCandidatesResult => ({
  updates: [...accumulator.updates, ...current.updates],
  inserts: [...accumulator.inserts, ...current.inserts],
})

// シートで範囲を選択してペーストしたり、deleteキーで削除したりするときに用いられる引数を生成する
export const generateChangeCandidates = (
  argument: GenerateChangeCandidatesArgument,
): { updates: UpdateChangeCandidate[]; inserts: InsertChangeCandidate[] } => {
  const { cursor } = argument
  if (cursor === undefined) {
    return { updates: [], inserts: [] }
  }
  const area = getArea(cursor.main, cursor.expand ?? cursor.main)
  return pipe(
    zipColumns(argument),
    Array.map(([copiedRowsForColumn, column]) =>
      generateChangeArgumentsForColumn({
        initialRowIndex: area.upRowIndex,
        initialInnerRowIndex: area.upInnerRowIndex,
        copiedRowsForColumn,
        records: argument.records,
        column,
        targetReferencedStreamName: argument.getReferencedStreamName(column),
      }),
    ),
    Array.reduce(emptyChangeCandidatesResult(), mergeChangeCandidatesResult),
  )
}

// copyMatrix の列とペースト先のカラムを対応付ける。ペースト先のカラムは、選択範囲の左端のカラムから順に、copyMatrix の列数分だけ取る
const zipColumns = (
  argument: GenerateChangeCandidatesArgument,
): Array<[TransposedCopyMatrix['columns'][number], RSheetColumn<RSheetRecordNode>]> => {
  const { copyMatrix, records, columns, cursor } = argument

  if (cursor === undefined || copyMatrix.rows.length === 0) {
    return []
  }

  const updatedCopyMatrix = expandMatrix(copyMatrix, cursor, records, columns)
  const copyMatrixWidth = updatedCopyMatrix.rows[0]?.columns.length ?? 0
  const transposedCopyMatrix = transposeMatrix(updatedCopyMatrix)
  const area = getArea(cursor.main, cursor.expand ?? cursor.main)
  const targetColumns = columns.slice(area.leftColumnIndex, area.leftColumnIndex + copyMatrixWidth)

  return pipe(transposedCopyMatrix.columns, Array.zip(targetColumns))
}

// copyMatrix の列とペースト先のカラムから、更新と挿入の候補を生成する。
// ここで、候補の height, innerRowIndex はコピー完了時の値になるように計算している。
// ペーストについては、後続の処理でこれらを用いてレコードの高さや innerRowIndex を調整しているため重要。
function generateChangeArgumentsForColumn({
  initialRowIndex,
  initialInnerRowIndex,
  copiedRowsForColumn,
  records,
  column,
  targetReferencedStreamName,
}: {
  initialRowIndex: number
  initialInnerRowIndex: number
  copiedRowsForColumn: TransposedCopyMatrix['columns'][number]
  records: RSheetRecordNode[]
  column: RSheetColumn<RSheetRecordNode>
  targetReferencedStreamName: string | undefined
}): { updates: UpdateChangeCandidate[]; inserts: InsertChangeCandidate[] } {
  return pipe(
    copiedRowsForColumn.rows,
    Array.map((copiedRow, copiedRowIndex) =>
      generateChangeArgumentsForCopiedRow({
        initialRowIndex,
        initialInnerRowIndex,
        copiedRow,
        copiedRowIndex,
        records,
        column,
        targetReferencedStreamName,
      }),
    ),
    Array.reduce(emptyChangeCandidatesResult(), mergeChangeCandidatesResult),
  )
}

const generateChangeArgumentsForCopiedRow = ({
  initialRowIndex,
  initialInnerRowIndex,
  copiedRow,
  copiedRowIndex,
  records,
  column,
  targetReferencedStreamName,
}: {
  initialRowIndex: number
  initialInnerRowIndex: number
  copiedRow: TransposedCopyMatrixRow
  copiedRowIndex: number
  records: RSheetRecordNode[]
  column: RSheetColumn<RSheetRecordNode>
  targetReferencedStreamName: string | undefined
}): ChangeCandidatesResult => {
  const rootRowIndex = initialRowIndex + copiedRowIndex
  // eslint-disable-next-line security/detect-object-injection -- rootRowIndex is computed from cursor area and copied row offset
  const rootRow = records[rootRowIndex]
  if (rootRow === undefined) {
    return generateInsertCandidatesForMissingRootRow({ rootRowIndex, copiedRow, column, targetReferencedStreamName })
  }

  const targetInnerRowIndexStart = copiedRowIndex === 0 ? initialInnerRowIndex : 0
  const targetRecordGroup = pipe(
    getRecordNodesWithInnerRowIndex(rootRow, column.node),
    Array.filter(
      (x) =>
        x.innerRowIndexStart >= targetInnerRowIndexStart &&
        !(x.parentRecordNode === undefined && x.recordNode === undefined),
    ),
  )

  const sourceGroups = splitByCellsPerParent(copiedRow.cellGroup, copiedRow.cellsPerParent)
  const targetGroups = groupByParentRecordNode(targetRecordGroup)

  return generateChangeArgumentsForExistingRootRow({
    sourceGroups,
    targetGroups,
    targetInnerRowIndexStart,
    rootRowIndex,
    column,
    targetReferencedStreamName,
  })
}

const generateInsertCandidatesForMissingRootRow = ({
  rootRowIndex,
  copiedRow,
  column,
  targetReferencedStreamName,
}: {
  rootRowIndex: number
  copiedRow: TransposedCopyMatrixRow
  column: RSheetColumn<RSheetRecordNode>
  targetReferencedStreamName: string | undefined
}): ChangeCandidatesResult => {
  if (column.nodeType === 'root') {
    return {
      updates: [],
      inserts: copiedRow.cellGroup.map((copiedCell, copiedCellIndex) => ({
        column,
        value: copiedCell.value,
        relationPaste: resolveRelationPaste(copiedCell, column, targetReferencedStreamName),
        rowIndex: rootRowIndex + copiedCellIndex,
        innerRowIndex: 0,
        height: copiedCell.height,
      })),
    }
  }

  const newCellGroup = column.nodeType === 'children' ? copiedRow.cellGroup : copiedRow.cellGroup.slice(0, 1)
  return {
    updates: [],
    inserts: pipe(
      newCellGroup,
      Array.mapAccum(0, (insertInnerRowIndex, copiedCell) => [
        insertInnerRowIndex + copiedCell.height,
        {
          column,
          value: copiedCell.value,
          relationPaste: resolveRelationPaste(copiedCell, column, targetReferencedStreamName),
          rowIndex: rootRowIndex,
          innerRowIndex: insertInnerRowIndex,
          height: copiedCell.height,
        },
      ]),
      ([, inserts]) => inserts,
    ),
  }
}

const generateChangeArgumentsForExistingRootRow = ({
  sourceGroups,
  targetGroups,
  targetInnerRowIndexStart,
  rootRowIndex,
  column,
  targetReferencedStreamName,
}: {
  sourceGroups: CellGroup[]
  targetGroups: RecordNodeWithInnerRowIndex[][]
  targetInnerRowIndexStart: number
  rootRowIndex: number
  column: RSheetColumn<RSheetRecordNode>
  targetReferencedStreamName: string | undefined
}): ChangeCandidatesResult => {
  const initialState = {
    result: emptyChangeCandidatesResult(),
    offset: 0,
    nextInsertInnerRowIndex: targetInnerRowIndexStart,
  }

  const finalState = pipe(
    sourceGroups,
    Array.mapAccum(initialState, (state, sourceGroup, groupIndex) => {
      // eslint-disable-next-line security/detect-object-injection -- groupIndex is a safe loop counter generated from sourceGroups
      const targetGroup = targetGroups[groupIndex] ?? []
      const { nextOffset: updateOffset, updates } = createUpdatesForGroup({
        targetGroup,
        sourceGroup,
        offset: state.offset,
        column,
        rootRowIndex,
        targetReferencedStreamName,
      })

      const remainingCopiedCells = sourceGroup.slice(updates.length)
      if (column.nodeType === 'children' && remainingCopiedCells.length > 0) {
        const startInnerRowIndex = calculateInsertStartInnerRowIndex(updates, state.nextInsertInnerRowIndex)
        const { insertEndInnerRowIndex, inserts } = createInsertsForGroup({
          copiedCells: remainingCopiedCells,
          startInnerRowIndex,
          column,
          rootRowIndex,
          targetReferencedStreamName,
        })

        return [
          {
            result: mergeChangeCandidatesResult(state.result, { updates, inserts }),
            offset: updateOffset + (insertEndInnerRowIndex - startInnerRowIndex),
            nextInsertInnerRowIndex: insertEndInnerRowIndex,
          },
          undefined,
        ]
      }

      const lastTarget = targetGroup.at(-1)
      return [
        {
          result: mergeChangeCandidatesResult(state.result, { updates, inserts: [] }),
          offset: updateOffset,
          nextInsertInnerRowIndex:
            lastTarget === undefined ? state.nextInsertInnerRowIndex : lastTarget.innerRowIndexEnd + updateOffset,
        },
        undefined,
      ]
    }),
    ([state]) => state,
  )

  return finalState.result
}

export const createUpdatesForGroup = ({
  targetGroup,
  sourceGroup,
  offset,
  column,
  rootRowIndex,
  targetReferencedStreamName,
}: {
  targetGroup: RecordNodeWithInnerRowIndex[]
  sourceGroup: TransposedCopyMatrixRow['cellGroup']
  offset: number
  column: RSheetColumn<RSheetRecordNode>
  rootRowIndex: number
  targetReferencedStreamName: string | undefined
}): { nextOffset: number; updates: UpdateChangeCandidate[] } => {
  const [nextOffset, updates] = pipe(
    targetGroup,
    Array.zip(sourceGroup),
    Array.mapAccum(offset, (currentOffset, [nodeWithInnerRowIndex, copiedCell]) => {
      const height = calcCellHeight(copiedCell.height, nodeWithInnerRowIndex.height)
      return [
        currentOffset + (height - nodeWithInnerRowIndex.height),
        {
          column,
          value: copiedCell.value,
          relationPaste: resolveRelationPaste(copiedCell, column, targetReferencedStreamName),
          rowIndex: rootRowIndex,
          innerRowIndex: nodeWithInnerRowIndex.innerRowIndexStart + currentOffset,
          height,
          recordId: nodeWithInnerRowIndex.recordNode?.id,
        },
      ]
    }),
  )

  return { nextOffset, updates }
}

const calculateInsertStartInnerRowIndex = (
  updates: UpdateChangeCandidate[],
  defaultStartInnerRowIndex: number,
): number => {
  const lastUpdateItem = updates.at(-1)
  return lastUpdateItem === undefined ? defaultStartInnerRowIndex : lastUpdateItem.innerRowIndex + lastUpdateItem.height
}

export const createInsertsForGroup = ({
  copiedCells,
  startInnerRowIndex,
  column,
  rootRowIndex,
  targetReferencedStreamName,
}: {
  copiedCells: TransposedCopyMatrixRow['cellGroup']
  startInnerRowIndex: number
  column: RSheetColumn<RSheetRecordNode>
  rootRowIndex: number
  targetReferencedStreamName: string | undefined
}): { insertEndInnerRowIndex: number; inserts: InsertChangeCandidate[] } => {
  const [insertEndInnerRowIndex, inserts] = pipe(
    copiedCells,
    Array.mapAccum(startInnerRowIndex, (innerRowIndex, copiedCell) => [
      innerRowIndex + copiedCell.height,
      {
        column,
        value: copiedCell.value,
        relationPaste: resolveRelationPaste(copiedCell, column, targetReferencedStreamName),
        rowIndex: rootRowIndex,
        innerRowIndex,
        height: copiedCell.height,
      },
    ]),
  )

  return { insertEndInnerRowIndex, inserts }
}

// コピーされた値が1行の場合は、選択範囲全体に同じ値でペーストする
function expandMatrix(
  copyMatrix: CopyMatrix,
  cursor: RSheetsCursor,
  data: RSheetRecordNode[],
  columns: Array<RSheetColumn<RSheetRecordNode>>,
): CopyMatrix {
  const { main, expand } = cursor
  if (expand === undefined) {
    return copyMatrix
  }

  const [firstRow] = copyMatrix.rows
  if (firstRow === undefined || copyMatrix.rows.length !== 1) {
    return copyMatrix
  }
  const area = getArea(main, expand)
  const firstRowColumns = firstRow.columns
  if (firstRowColumns.some((column) => column.cellGroup.length === 0)) {
    return copyMatrix
  }

  const rootRowCount = area.downRowIndex - area.upRowIndex + 1
  const expandedMatrix: CopyMatrix = {
    rows: Array.range(0, rootRowCount - 1).map((rowOffset) => {
      const rowIndex = area.upRowIndex + rowOffset
      return {
        columns: firstRowColumns.map((sourceColumn, columnIndex) => {
          // ペースト対象となる列の取得（index...選択範囲の左端+列オフセット）
          const column = columns[area.leftColumnIndex + columnIndex]
          const [firstSourceCell] = sourceColumn.cellGroup
          if (firstSourceCell === undefined) {
            return sourceColumn
          }

          if (sourceColumn.cellGroup.length > 1) {
            return {
              cellGroup: [...sourceColumn.cellGroup],
              cellsPerParent: [...sourceColumn.cellsPerParent],
            }
          }

          const repeatCount = column === undefined ? 1 : calculateCellCountByRootRow(column, data, area, rowIndex)
          return {
            // salescoreValue（参照列の id 解決用リッチ値）を落とさず引き継ぐ
            cellGroup: Array.range(0, repeatCount - 1).map(() => ({ ...firstSourceCell })),
            cellsPerParent: [repeatCount],
          }
        }),
      }
    }),
  }
  return expandedMatrix
}

const calculateCellCountByRootRow = (
  column: RSheetColumn<RSheetRecordNode>,
  data: RSheetRecordNode[],
  area: ExpandCursorArea,
  rowIndex: number,
): number => {
  if (column.node.path.length <= 1) {
    return 1
  }

  // eslint-disable-next-line security/detect-object-injection -- rowIndex is computed from selected area
  const row = data[rowIndex]
  if (!isNotUndefined(row)) {
    return 1
  }

  const rowNodes = pipe(
    getRecordNodesWithInnerRowIndex(row, column.node),
    Array.filter((node) => !(node.parentRecordNode === undefined && node.recordNode === undefined)),
    Array.filter((node) => rowIndex !== area.upRowIndex || node.innerRowIndexStart >= area.upInnerRowIndex),
    Array.filter((node) => rowIndex !== area.downRowIndex || node.innerRowIndexStart <= area.downInnerRowIndex),
  )

  return Math.max(1, rowNodes.length)
}

const splitByCellsPerParent = <T>(items: T[], cellsPerParent: number[]): T[][] =>
  pipe(
    cellsPerParent,
    Array.mapAccum(0, (offset, size) => [offset + size, items.slice(offset, offset + size)]),
    ([, groups]) => groups,
  )

const groupByParentRecordNode = (nodes: RecordNodeWithInnerRowIndex[]): RecordNodeWithInnerRowIndex[][] =>
  pipe(
    nodes,
    Array.reduce<RecordNodeWithInnerRowIndex[][], RecordNodeWithInnerRowIndex>([], (groups, node) => {
      const lastGroup = groups.at(-1)
      if (lastGroup === undefined) {
        return [[node]]
      }

      const lastNode = lastGroup.at(-1)
      if (lastNode !== undefined && lastNode.parentRecordNode === node.parentRecordNode) {
        return [...groups.slice(0, -1), [...lastGroup, node]]
      }

      return [...groups, [node]]
    }),
  )
